import {Button, Checkbox} from '@wandb/weave/components';
import * as Dialog from '@wandb/weave/components/Dialog';
import {Icon} from '@wandb/weave/components/Icon';
import {
  maybePluralize,
  maybePluralizeWord,
} from '@wandb/weave/core/util/string';
import React, {useMemo, useState} from 'react';
import {toast} from 'react-toastify';

import {
  useDataRetentionDurationQuery,
  useDeleteRunChildrenQuery,
  useRunArtifactVersionsQuery,
  useRunHasProtectedOutputArtifactQuery,
} from '../generated/graphql';
import {Analytics} from '../services/analytics';
import {useDeleteRun} from '../state/runs/hooks';
import {propagateErrorsContext} from './../util/errors';
import {Banner, BannerFlexWrapper, BannerTextWrapper} from './Banner';

const DAY = 24 * 3600 * 1000;

interface DeleteRunModalProps {
  entityName: string;
  isRunActive: boolean;
  projectName: string;
  runDisplayName: string;
  runId: string;
  runName: string;
  trigger?: React.ReactNode;
  isOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  closeModal?(): void;
}

const DeleteRunModal: React.FC<DeleteRunModalProps> = React.memo(
  ({
    isOpen,
    closeModal,
    setIsModalOpen,
    entityName,
    isRunActive,
    projectName,
    runDisplayName,
    runId,
    runName,
  }) => {
    const {
      data: runHasProtectedOutputArtifactData,
      loading: runHasProtectedArtifactLoading,
    } = useRunHasProtectedOutputArtifactQuery({
      variables: {entityName, projectName, runName},
      skip: !isOpen,
      context: propagateErrorsContext(),
    });

    const hasProtectedArtifact = useMemo(() => {
      const runHasProtectedOutputArtifact =
        runHasProtectedOutputArtifactData?.project?.run
          ?.hasProtectedOutputArtifact ?? false;
      if (runHasProtectedOutputArtifact) {
        Analytics.track('source_artifact_protection', {
          location: 'DeleteRunModal',
          entityName,
          projectName,
        });
      }
      return runHasProtectedOutputArtifact;
    }, [runHasProtectedOutputArtifactData, entityName, projectName]);

    const {data: childrenData} = useDeleteRunChildrenQuery({
      variables: {entityName, projectName, runName},
      skip: !isOpen,
      fetchPolicy: 'no-cache',
    });
    const childRuns = childrenData?.project?.run?.descendantRuns?.runs ?? [];

    const [deleteArtifacts, setDeleteArtifacts] = useState(false);
    const deleteRun = useDeleteRun();

    const handleDeleteRun = React.useCallback(() => {
      deleteRun(runName, {
        context: {
          ...propagateErrorsContext(),
        },
        variables: {
          id: runId,
          deleteArtifacts,
        },
      })
        .then(result => {
          const numDeleted = result?.data?.deleteRun?.numDeleted ?? 1;
          const message =
            numDeleted > 1
              ? `Run ${runDisplayName} and ${numDeleted - 1} descendent run(s) deleted`
              : `Run ${runDisplayName} deleted`;
          toast.success(message, {
            autoClose: 2500,
          });
        })
        .catch(err => {
          console.error('Error deleting run', {
            deleteArtifacts,
            entityName,
            projectName,
            runId,
          });
          toast.error(
            `Unable to delete run: ${runDisplayName}. Please try again. `
          );
        })
        .finally(() => {
          closeModal?.();
        });
    }, [
      closeModal,
      deleteArtifacts,
      deleteRun,
      runId,
      runName,
      entityName,
      projectName,
      runDisplayName,
    ]);

    const runArtifacts = useRunArtifactVersionsQuery({
      variables: {
        entityName,
        projectName,
        runName,
        includeOutput: true,
        includeInput: false,
        pageSize: 1, // we only care about the total count, so keep the size low
      },
      fetchPolicy: 'no-cache',
      skip: !isOpen,
    });

    const outputArtifactsCount =
      runArtifacts.data?.project?.run?.outputArtifacts?.totalCount ?? 0;
    const outputArtifacts =
      runArtifacts.data?.project?.run?.outputArtifacts?.edges;
    let countArtifactsLinkedToRegistry = 0;
    outputArtifacts?.forEach(artifact => {
      if (artifact.node?.isLinkedToGlobalRegistry) {
        countArtifactsLinkedToRegistry += 1;
      }
    });

    const showCheckbox = (outputArtifactsCount ?? 0) > 0;

    const dataRetentionDurationQuery = useDataRetentionDurationQuery({});
    const dataRetentionDurationMs =
      dataRetentionDurationQuery.data?.serverInfo?.dataRetentionDuration ??
      7 * DAY;
    const dataRetentionDays = Math.ceil(dataRetentionDurationMs / DAY);

    const onOpenChange = (open: boolean) => {
      setIsModalOpen(open);
      setDeleteArtifacts(false);
    };

    return (
      <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay />
          <Dialog.Content
            className="w-[540px] rounded-lg"
            onInteractOutside={e => {
              e.preventDefault();
            }}>
            <Dialog.Title>Delete this run?</Dialog.Title>
            <Dialog.Description className="mt-16">
              {isRunActive ? (
                <span>
                  You're deleting an active run, which might crash live
                  processes if your run is actually still running. We suggest
                  that you stop the run before deleting it.
                </span>
              ) : (
                <span>
                  Delete run "{runDisplayName}"? This affects all pages,
                  including reports.{' '}
                  {dataRetentionDays > 0
                    ? `This run will be permanently deleted and unrecoverable in
              ${maybePluralize(dataRetentionDays, 'day')}.`
                    : `This run will be unrecoverable after 7 days.`}
                </span>
              )}
            </Dialog.Description>
            {childRuns.length > 0 && (
              <Dialog.Description className="mt-16">
                <span className="font-['Source_Sans_3'] font-semibold">
                  {maybePluralize(childRuns.length, 'descendant run')} will also
                  be deleted.
                </span>
                <pre className="mt-8 max-h-[400px] overflow-y-auto rounded bg-moon-150 dark:bg-moon-800 p-8 font-['Source_Code_Pro'] text-sm">
                  {childRuns
                    .map(child => child.displayName ?? child.name)
                    .join('\n')}
                </pre>
              </Dialog.Description>
            )}
            <Dialog.Description className="mt-16">
              {showCheckbox && (
                <div>
                  <div className="flex gap-8">
                    <div className="mb-auto mt-4">
                      <Checkbox
                        size="small"
                        checked={deleteArtifacts}
                        disabled={
                          hasProtectedArtifact || runHasProtectedArtifactLoading
                        }
                        onClick={() => setDeleteArtifacts(!deleteArtifacts)}
                      />
                    </div>
                    <span
                      className={
                        hasProtectedArtifact ? 'text-wb-text-tertiary' : ''
                      }>
                      Also delete the{' '}
                      {maybePluralize(outputArtifactsCount, 'artifact')} this
                      run created. Any references to{' '}
                      {outputArtifactsCount > 1 ? 'these' : 'this'}{' '}
                      {maybePluralizeWord(outputArtifactsCount, 'artifact')}{' '}
                      will be broken.
                    </span>
                  </div>
                  {deleteArtifacts && countArtifactsLinkedToRegistry > 0 && (
                    <Banner variant="error" className="mt-16">
                      <BannerFlexWrapper>
                        <div>
                          <Icon name="warning" />
                        </div>
                        <BannerTextWrapper>{`${
                          countArtifactsLinkedToRegistry > 1
                            ? `${countArtifactsLinkedToRegistry} artifacts are`
                            : 'This artifact is'
                        } linked to a registry, from which ${
                          countArtifactsLinkedToRegistry > 1 ? 'they' : 'it'
                        } will also be deleted. References to linked versions will also be broken.`}</BannerTextWrapper>
                      </BannerFlexWrapper>
                    </Banner>
                  )}
                </div>
              )}
              {hasProtectedArtifact && (
                <Banner variant="warning" className="mt-16">
                  <BannerFlexWrapper>
                    <div>
                      <Icon name="warning" />
                    </div>
                    <BannerTextWrapper>
                      Artifact deletion cannot be enabled. You are unable to
                      delete select artifacts until all their versions have been
                      unlinked from protected registry collections.
                    </BannerTextWrapper>
                  </BannerFlexWrapper>
                </Banner>
              )}
            </Dialog.Description>
            <div className="mb-8 mt-24 flex items-center">
              <Button
                variant="destructive"
                size="large"
                disabled={
                  runArtifacts.loading ||
                  ((hasProtectedArtifact || runHasProtectedArtifactLoading) &&
                    deleteArtifacts)
                }
                onClick={handleDeleteRun}>
                {isRunActive ? `Yes, delete` : `Delete`}
              </Button>
              <Dialog.Close className="ml-8" asChild>
                <Button variant="ghost" size="large">
                  Cancel
                </Button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }
);

export default DeleteRunModal;
